import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { cancelUserTagRecalculation, checkUserTagNameExists, createUserTag, getUserTag, getUserTagOptions, recalculateUserTag, updateUserTag } from '../Api';
import UserTagDefinitionOptionsDto, {
    PredefinedUserTagValueDto,
    UserTagRequirementDataType,
    UserTagRequirementTypeInfoDto,
} from '../models/dtos/UserTagDefinitionOptionsDto';
import UserTagDefinitionDto, { UpsertUserTagDefinitionRequest, UserTagRequirementDto, UserTagRequirementRequest } from '../models/dtos/UserTagDefinitionDto';
import UserTagRecalculationStatus from '../components/UserTagRecalculationStatus';

type DurationParts = {
    days: string;
    hours: string;
    minutes: string;
}

type RequirementFormState = {
    id: string;
    type: string;
    operator: string;
    textValue: string;
    textValues: string;
    selectedPredefinedKeys: string[];
    durationValue: DurationParts;
    durationValues: DurationParts[];
}

const TAG_NAME_PATTERN = /^[0-9A-Za-z_]+$/;
const RECALCULATION_POLL_INTERVAL_MS = 10_000;
const shouldPollTagRecalculationStatus = (tag: UserTagDefinitionDto | null) => {
    if (tag == null) return false;
    return tag.tagRecalculationInfo != null || tag.approximateUsersCount == null;
};

const createEmptyDuration = (): DurationParts => ({
    days: '0',
    hours: '0',
    minutes: '0',
});

const createRequirementId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const humanizeEnum = (value: string) => value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getOperatorLabel = (operator: string) => {
    switch (operator) {
        case 'GTE':
            return 'Greater than or equal';
        case 'LTE':
            return 'Less than or equal';
        case 'EQ':
            return 'Equals';
        case 'NEQ':
            return 'Does not equal';
        case 'IN':
            return 'In list';
        case 'NIN':
            return 'Not in list';
        case 'BETWEEN':
            return 'Between';
        default:
            return operator;
    }
};

const isMultiValueOperator = (operator: string) => operator === 'IN' || operator === 'NIN' || operator === 'BETWEEN';

const getPredefinedOptionKey = (option: PredefinedUserTagValueDto) => {
    if (option.stringValue != null) return option.stringValue;
    if (option.numericValue != null) return String(option.numericValue);
    if (option.durationValue != null) return String(option.durationValue);
    return option.displayName;
};

const getDurationFromMinutes = (minutes: number): DurationParts => {
    const safeValue = Math.max(0, Math.floor(minutes));
    const days = Math.floor(safeValue / (24 * 60));
    const hours = Math.floor((safeValue % (24 * 60)) / 60);
    const remainingMinutes = safeValue % 60;

    return {
        days: String(days),
        hours: String(hours),
        minutes: String(remainingMinutes),
    };
};

const getMinutesFromDuration = (value: DurationParts): number | null => {
    const days = parseInteger(value.days);
    const hours = parseInteger(value.hours);
    const minutes = parseInteger(value.minutes);

    if (days == null || hours == null || minutes == null) return null;
    if (days < 0 || hours < 0 || minutes < 0) return null;
    if (hours > 23 || minutes > 59) return null;

    return days * 24 * 60 + hours * 60 + minutes;
};

const parseInteger = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return 0;
    if (!/^\d+$/.test(trimmed)) return null;
    return parseInt(trimmed, 10);
};

const parseNumericValue = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseNumericValues = (value: string): number[] | null => {
    const items = value.split(',').map(item => item.trim()).filter(Boolean);
    if (items.length === 0) return null;

    const parsedValues = items.map(item => Number(item));
    if (parsedValues.some(item => !Number.isFinite(item))) return null;

    return parsedValues;
};

const parseStringValues = (value: string): string[] => {
    return value.split(',').map(item => item.trim()).filter(Boolean);
};

const getOperatorsForType = (options: UserTagDefinitionOptionsDto, dataType: UserTagRequirementDataType): string[] => {
    switch (dataType) {
        case 'numeric':
            return options.numericOperators;
        case 'duration':
            return options.durationOperators;
        case 'string':
            return options.stringOperators;
        default:
            return [];
    }
};

const getDefaultOperator = (typeInfo: UserTagRequirementTypeInfoDto, options: UserTagDefinitionOptionsDto): string => {
    const operators = getOperatorsForType(options, typeInfo.dataType);
    if (typeInfo.dataType === 'string' && typeInfo.predefinedValues.length > 0 && operators.includes('IN')) {
        return 'IN';
    }
    return operators[0] ?? '';
};

const createRequirementState = (typeInfo: UserTagRequirementTypeInfoDto, options: UserTagDefinitionOptionsDto): RequirementFormState => ({
    id: createRequirementId(),
    type: typeInfo.key,
    operator: getDefaultOperator(typeInfo, options),
    textValue: '',
    textValues: '',
    selectedPredefinedKeys: [],
    durationValue: createEmptyDuration(),
    durationValues: [createEmptyDuration()],
});

const createRequirementStateFromDto = (
    requirement: UserTagRequirementDto,
    typeInfo: UserTagRequirementTypeInfoDto,
    options: UserTagDefinitionOptionsDto,
): RequirementFormState => {
    const baseState = createRequirementState(typeInfo, options);
    const hasPredefinedValues = typeInfo.predefinedValues.length > 0;
    const operator = requirement.operator;

    if (hasPredefinedValues) {
        const selectedPredefinedKeys = isMultiValueOperator(operator)
            ? typeInfo.predefinedValues
                .filter(option =>
                    (option.stringValue != null && requirement.stringValues.includes(option.stringValue)) ||
                    (option.numericValue != null && requirement.numericValues.includes(option.numericValue)) ||
                    (option.durationValue != null && requirement.durationValuesMinutes.includes(option.durationValue))
                )
                .map(getPredefinedOptionKey)
            : typeInfo.predefinedValues
                .filter(option =>
                    (option.stringValue != null && option.stringValue === requirement.stringValue) ||
                    (option.numericValue != null && option.numericValue === requirement.numericValue) ||
                    (option.durationValue != null && option.durationValue === requirement.durationValueMinutes)
                )
                .map(getPredefinedOptionKey);

        return {
            ...baseState,
            operator,
            selectedPredefinedKeys,
        };
    }

    if (typeInfo.dataType === 'duration') {
        return {
            ...baseState,
            operator,
            durationValue: getDurationFromMinutes(requirement.durationValueMinutes ?? 0),
            durationValues: requirement.durationValuesMinutes.length > 0
                ? requirement.durationValuesMinutes.map(getDurationFromMinutes)
                : [createEmptyDuration()],
        };
    }

    if (typeInfo.dataType === 'numeric') {
        return {
            ...baseState,
            operator,
            textValue: requirement.numericValue != null ? String(requirement.numericValue) : '',
            textValues: requirement.numericValues.join(', '),
        };
    }

    return {
        ...baseState,
        operator,
        textValue: requirement.stringValue ?? '',
        textValues: requirement.stringValues.join(', '),
    };
};

export default function CreateUserTagPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = id != null;
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [recalculating, setRecalculating] = React.useState(false);
    const [cancelingRecalculation, setCancelingRecalculation] = React.useState(false);
    const [options, setOptions] = React.useState<UserTagDefinitionOptionsDto | null>(null);
    const [tagName, setTagName] = React.useState('');
    const [originalTagName, setOriginalTagName] = React.useState('');
    const [loadedTag, setLoadedTag] = React.useState<UserTagDefinitionDto | null>(null);
    const [requirements, setRequirements] = React.useState<RequirementFormState[]>([]);
    const [generalError, setGeneralError] = React.useState<string | null>(null);
    const [showValidation, setShowValidation] = React.useState(false);
    const [nameCheckState, setNameCheckState] = React.useState({
        checking: false,
        exists: false,
        checkedName: '',
    });
    const [shouldPollRecalculationStatus, setShouldPollRecalculationStatus] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            const [optionsResponse, tagResponse] = await Promise.all([
                getUserTagOptions(),
                isEditMode && id ? getUserTag(id) : Promise.resolve(null),
            ]);
            if (cancelled) return;

            if (optionsResponse == null) {
                setGeneralError('Failed to load user tag builder options.');
                setLoading(false);
                return;
            }

            if (isEditMode && id && tagResponse == null) {
                setGeneralError('Failed to load user tag.');
                setLoading(false);
                return;
            }

            setOptions(optionsResponse);

            if (tagResponse != null) {
                setLoadedTag(tagResponse);
                setShouldPollRecalculationStatus(shouldPollTagRecalculationStatus(tagResponse));
                setTagName(tagResponse.tagName);
                setOriginalTagName(tagResponse.tagName);
                const mappedRequirements = tagResponse.requirements
                    .map(requirement => {
                        const typeInfo = optionsResponse.requirementTypes.find(item => item.key === requirement.type);
                        return typeInfo == null ? null : createRequirementStateFromDto(requirement, typeInfo, optionsResponse);
                    })
                    .filter((requirement): requirement is RequirementFormState => requirement != null);
                setRequirements(mappedRequirements.length > 0
                    ? mappedRequirements
                    : (optionsResponse.requirementTypes.length > 0 ? [createRequirementState(optionsResponse.requirementTypes[0], optionsResponse)] : []));
            } else if (optionsResponse.requirementTypes.length > 0) {
                setLoadedTag(null);
                setShouldPollRecalculationStatus(false);
                setRequirements([createRequirementState(optionsResponse.requirementTypes[0], optionsResponse)]);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [id, isEditMode]);

    React.useEffect(() => {
        if (!isEditMode || id == null || !shouldPollRecalculationStatus) {
            return;
        }

        const intervalId = window.setInterval(async () => {
            const tagResponse = await getUserTag(id);
            if (tagResponse == null) {
                return;
            }

            setLoadedTag(tagResponse);
            if (!shouldPollTagRecalculationStatus(tagResponse)) {
                setShouldPollRecalculationStatus(false);
            }
        }, RECALCULATION_POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [id, isEditMode, shouldPollRecalculationStatus]);

    React.useEffect(() => {
        const trimmedName = tagName.trim();
        if (isEditMode && trimmedName === originalTagName) {
            setNameCheckState({ checking: false, exists: false, checkedName: trimmedName });
            return;
        }
        if (!trimmedName || !TAG_NAME_PATTERN.test(trimmedName)) {
            setNameCheckState({ checking: false, exists: false, checkedName: '' });
            return;
        }

        let cancelled = false;
        setNameCheckState(prev => ({ ...prev, checking: true, exists: false }));

        const timeoutId = window.setTimeout(async () => {
            const result = await checkUserTagNameExists(trimmedName);
            if (cancelled) return;

            setNameCheckState({
                checking: false,
                exists: result?.exists ?? false,
                checkedName: trimmedName,
            });
        }, 400);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [tagName, isEditMode, originalTagName]);

    const getTypeInfo = (type: string) => options?.requirementTypes.find(item => item.key === type) ?? null;

    const getTypeInfos = () => {
        if (options == null) return [];
        return options.requirementTypes;
    };

    const onRequirementTypeChanged = (requirementId: string, type: string) => {
        if (options == null) return;

        const typeInfo = options.requirementTypes.find(item => item.key === type);
        if (typeInfo == null) return;

        setRequirements(prevRequirements => prevRequirements.map(requirement =>
            requirement.id === requirementId
                ? { ...createRequirementState(typeInfo, options), id: requirementId }
                : requirement
        ));
    };

    const onRequirementOperatorChanged = (requirementId: string, operator: string) => {
        setRequirements(prevRequirements => prevRequirements.map(requirement => {
            if (requirement.id !== requirementId) return requirement;

            const wasMultiValue = isMultiValueOperator(requirement.operator);
            const isMultiValue = isMultiValueOperator(operator);
            const preservedPredefinedKeys = isMultiValue
                ? requirement.selectedPredefinedKeys
                : requirement.selectedPredefinedKeys.slice(0, 1);

            const preservedTextValues = wasMultiValue
                ? requirement.textValues
                : requirement.textValue;
            const preservedTextValue = wasMultiValue
                ? (requirement.textValues.split(',')[0]?.trim() ?? '')
                : requirement.textValue;

            return {
                ...requirement,
                operator,
                selectedPredefinedKeys: preservedPredefinedKeys,
                textValue: isMultiValue ? '' : preservedTextValue,
                textValues: isMultiValue
                    ? (wasMultiValue ? preservedTextValues : preservedTextValue)
                    : '',
                durationValue: isMultiValue
                    ? createEmptyDuration()
                    : (wasMultiValue ? requirement.durationValues[0] ?? createEmptyDuration() : requirement.durationValue),
                durationValues: isMultiValue
                    ? (wasMultiValue
                        ? requirement.durationValues
                        : [requirement.durationValue])
                    : [createEmptyDuration()],
            };
        }));
    };

    const updateRequirement = (requirementId: string, updater: (requirement: RequirementFormState) => RequirementFormState) => {
        setRequirements(prevRequirements => prevRequirements.map(requirement =>
            requirement.id === requirementId ? updater(requirement) : requirement
        ));
    };

    const addRequirement = () => {
        if (options == null) return;

        const nextTypeInfo = getTypeInfos()[0];
        if (nextTypeInfo == null) return;

        setRequirements(prevRequirements => [...prevRequirements, createRequirementState(nextTypeInfo, options)]);
    };

    const removeRequirement = (requirementId: string) => {
        setRequirements(prevRequirements => prevRequirements.filter(requirement => requirement.id !== requirementId));
    };

    const getSelectedPredefinedOptions = (typeInfo: UserTagRequirementTypeInfoDto, requirement: RequirementFormState) => {
        return typeInfo.predefinedValues.filter(option => requirement.selectedPredefinedKeys.includes(getPredefinedOptionKey(option)));
    };

    const getTagNameError = () => {
        const trimmedName = tagName.trim();
        if (!showValidation && !nameCheckState.exists) return null;
        if (!trimmedName) return 'User tag name is required.';
        if (!TAG_NAME_PATTERN.test(trimmedName)) return 'Use only letters, numbers, and underscores.';
        if (nameCheckState.exists && nameCheckState.checkedName === trimmedName) return 'A user tag with this name already exists.';
        return null;
    };

    const buildRequirementRequest = (requirement: RequirementFormState): { request?: UserTagRequirementRequest; error?: string } => {
        const typeInfo = getTypeInfo(requirement.type);
        if (typeInfo == null) return { error: 'Requirement type is required.' };
        if (!requirement.operator) return { error: `Operator is required for ${humanizeEnum(requirement.type)}.` };

        const usesPredefinedValues = typeInfo.predefinedValues.length > 0;
        const selectedPredefinedOptions = getSelectedPredefinedOptions(typeInfo, requirement);

        if (typeInfo.dataType === 'numeric') {
            if (isMultiValueOperator(requirement.operator)) {
                const numericValues = usesPredefinedValues
                    ? selectedPredefinedOptions.map(option => option.numericValue).filter((value): value is number => value != null)
                    : parseNumericValues(requirement.textValues);

                if (numericValues == null || numericValues.length === 0) {
                    return { error: `Provide at least one numeric value for ${humanizeEnum(requirement.type)}.` };
                }
                if (requirement.operator === 'BETWEEN' && numericValues.length !== 2) {
                    return { error: `Provide exactly two numeric values (min,max) for ${humanizeEnum(requirement.type)}.` };
                }

                return {
                    request: {
                        type: requirement.type,
                        operator: requirement.operator,
                        numericValues,
                        durationValuesMinutes: [],
                        stringValues: [],
                    }
                };
            }

            const numericValue = usesPredefinedValues
                ? selectedPredefinedOptions[0]?.numericValue ?? null
                : parseNumericValue(requirement.textValue);

            if (numericValue == null) {
                return { error: `Provide a numeric value for ${humanizeEnum(requirement.type)}.` };
            }

            return {
                request: {
                    type: requirement.type,
                    operator: requirement.operator,
                    numericValue,
                    numericValues: [],
                    durationValuesMinutes: [],
                    stringValues: [],
                }
            };
        }

        if (typeInfo.dataType === 'duration') {
            if (isMultiValueOperator(requirement.operator)) {
                const durationValuesMinutes = usesPredefinedValues
                    ? selectedPredefinedOptions.map(option => option.durationValue).filter((value): value is number => value != null)
                    : requirement.durationValues.map(getMinutesFromDuration);

                if (durationValuesMinutes.length === 0 || durationValuesMinutes.some(value => value == null)) {
                    return { error: `Provide valid duration values for ${humanizeEnum(requirement.type)}.` };
                }
                if (requirement.operator === 'BETWEEN' && durationValuesMinutes.length !== 2) {
                    return { error: `Provide exactly two duration values (min,max) for ${humanizeEnum(requirement.type)}.` };
                }

                return {
                    request: {
                        type: requirement.type,
                        operator: requirement.operator,
                        numericValues: [],
                        durationValuesMinutes: durationValuesMinutes as number[],
                        stringValues: [],
                    }
                };
            }

            const durationValueMinutes = usesPredefinedValues
                ? selectedPredefinedOptions[0]?.durationValue ?? null
                : getMinutesFromDuration(requirement.durationValue);

            if (durationValueMinutes == null) {
                return { error: `Provide a valid duration for ${humanizeEnum(requirement.type)}.` };
            }

            return {
                request: {
                    type: requirement.type,
                    operator: requirement.operator,
                    numericValues: [],
                    durationValueMinutes,
                    durationValuesMinutes: [],
                    stringValues: [],
                }
            };
        }

        if (isMultiValueOperator(requirement.operator)) {
            const stringValues = usesPredefinedValues
                ? selectedPredefinedOptions.map(option => option.stringValue).filter((value): value is string => value != null && value.length > 0)
                : parseStringValues(requirement.textValues);

            if (stringValues.length === 0) {
                return { error: `Provide at least one value for ${humanizeEnum(requirement.type)}.` };
            }
            if (requirement.operator === 'BETWEEN') {
                return { error: 'BETWEEN is not supported for string requirements.' };
            }

            return {
                request: {
                    type: requirement.type,
                    operator: requirement.operator,
                    numericValues: [],
                    durationValuesMinutes: [],
                    stringValues,
                }
            };
        }

        const stringValue = usesPredefinedValues
            ? selectedPredefinedOptions[0]?.stringValue ?? null
            : requirement.textValue.trim();

        if (stringValue == null || stringValue.length === 0) {
            return { error: `Provide a value for ${humanizeEnum(requirement.type)}.` };
        }

        return {
            request: {
                type: requirement.type,
                operator: requirement.operator,
                numericValues: [],
                durationValuesMinutes: [],
                stringValue,
                stringValues: [],
            }
        };
    };

    const onSaveClicked = async () => {
        setShowValidation(true);
        setGeneralError(null);

        const trimmedName = tagName.trim();
        if (!trimmedName) {
            setGeneralError('User tag name is required.');
            return;
        }
        if (!TAG_NAME_PATTERN.test(trimmedName)) {
            setGeneralError('User tag name may contain only letters, numbers, and underscores.');
            return;
        }
        if (requirements.length === 0) {
            setGeneralError('At least one requirement is required.');
            return;
        }

        setSaving(true);

        if (!isEditMode || trimmedName !== originalTagName) {
            const nameCheck = await checkUserTagNameExists(trimmedName);
            if (nameCheck?.exists) {
                setNameCheckState({ checking: false, exists: true, checkedName: trimmedName });
                setGeneralError('A user tag with this name already exists.');
                setSaving(false);
                return;
            }
        }

        const builtRequirements: UserTagRequirementRequest[] = [];
        for (let index = 0; index < requirements.length; index++) {
            const result = buildRequirementRequest(requirements[index]);
            if (result.request == null) {
                setGeneralError(`Requirement ${index + 1}: ${result.error}`);
                setSaving(false);
                return;
            }
            builtRequirements.push(result.request);
        }

        const request: UpsertUserTagDefinitionRequest = {
            tagName: trimmedName,
            requirements: builtRequirements,
        };

        const response = isEditMode && id
            ? await updateUserTag(id, request)
            : await createUserTag(request);
        if (!response.ok) {
            setGeneralError(response.error ?? (isEditMode ? 'Failed to update user tag.' : 'Failed to create user tag.'));
            setSaving(false);
            return;
        }

        navigate('/marketing/user-tags');
    };

    const onRecalculateClicked = async () => {
        if (!isEditMode || id == null) {
            return;
        }

        setGeneralError(null);
        setRecalculating(true);

        const response = await recalculateUserTag(id);
        if (!response.ok) {
            setGeneralError(response.error ?? 'Failed to recalculate user tag.');
            setRecalculating(false);
            return;
        }

        if (response.data != null) {
            setLoadedTag(response.data);
            setShouldPollRecalculationStatus(true);
        }

        const latestTag = await getUserTag(id);
        if (latestTag != null) {
            setLoadedTag(latestTag);
            setShouldPollRecalculationStatus(shouldPollTagRecalculationStatus(latestTag));
        } else {
            setShouldPollRecalculationStatus(true);
        }

        setRecalculating(false);
    };

    const onCancelRecalculationClicked = async () => {
        if (!isEditMode || id == null) {
            return;
        }

        setGeneralError(null);
        setCancelingRecalculation(true);

        const response = await cancelUserTagRecalculation(id);
        if (!response.ok) {
            setGeneralError(response.error ?? 'Failed to cancel user tag recalculation.');
            setCancelingRecalculation(false);
            return;
        }

        if (response.data != null) {
            setLoadedTag(response.data);
            setShouldPollRecalculationStatus(false);
        } else {
            const latestTag = await getUserTag(id);
            if (latestTag != null) {
                setLoadedTag(latestTag);
            }
            setShouldPollRecalculationStatus(false);
        }

        setCancelingRecalculation(false);
    };

    const renderDurationInputs = (
        value: DurationParts,
        onChange: (field: keyof DurationParts, nextValue: string) => void,
    ) => {
        return (
            <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                    <TextField
                        label="Days"
                        type="number"
                        fullWidth
                        value={value.days}
                        onChange={(event) => onChange('days', event.target.value)}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        label="Hours"
                        type="number"
                        fullWidth
                        value={value.hours}
                        onChange={(event) => onChange('hours', event.target.value)}
                        inputProps={{ min: 0, max: 23 }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        label="Minutes"
                        type="number"
                        fullWidth
                        value={value.minutes}
                        onChange={(event) => onChange('minutes', event.target.value)}
                        inputProps={{ min: 0, max: 59 }}
                    />
                </Grid>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Stack gap={3} sx={{ mx: 5, my: 5 }} alignItems="center">
                <CircularProgress />
            </Stack>
        );
    }

    if (options == null) {
        return (
            <Stack gap={3} sx={{ mx: 5, my: 5 }}>
                <Alert severity="error">{generalError ?? 'Failed to load user tag builder.'}</Alert>
            </Stack>
        );
    }

    const tagNameError = getTagNameError();
    const canAddRequirement = options.requirementTypes.length > 0;

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                <Box>
                    <Typography variant="h4">{isEditMode ? 'Edit User Tag' : 'Create User Tag'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Build a reusable user tag by combining unique requirements. Duration values are edited as days, hours, and minutes and converted automatically for the API.
                    </Typography>
                </Box>
                <Stack direction="row" gap={2}>
                    <Button variant="outlined" onClick={() => navigate('/marketing/user-tags')} disabled={saving}>
                        Cancel
                    </Button>
                    {isEditMode && (
                        <Button
                            variant="outlined"
                            onClick={onRecalculateClicked}
                            disabled={saving || recalculating || cancelingRecalculation}
                        >
                            {recalculating ? 'Starting recalculation...' : 'Recalculate users'}
                        </Button>
                    )}
                    {isEditMode && loadedTag != null && (
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/users?userTags=' + encodeURIComponent(loadedTag.tagName))}
                            disabled={saving || recalculating || cancelingRecalculation}
                        >
                            View users
                        </Button>
                    )}
                    <Button variant="contained" onClick={onSaveClicked} disabled={saving || nameCheckState.checking}>
                        {saving ? 'Saving...' : (isEditMode ? 'Save changes' : 'Save user tag')}
                    </Button>
                </Stack>
            </Stack>

            {generalError && <Alert severity="error">{generalError}</Alert>}

            {isEditMode && loadedTag != null && (
                <Card>
                    <CardContent>
                        <Stack gap={1.5}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                gap={2}
                            >
                                <Typography variant="h6">Current recalculation status</Typography>
                                {loadedTag.tagRecalculationInfo != null && (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={onCancelRecalculationClicked}
                                        disabled={cancelingRecalculation || recalculating}
                                    >
                                        {cancelingRecalculation ? 'Cancelling...' : 'Cancel'}
                                    </Button>
                                )}
                            </Stack>
                            <UserTagRecalculationStatus tag={loadedTag} />
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <TextField
                        label="User tag name"
                        fullWidth
                        value={tagName}
                        onChange={(event) => setTagName(event.target.value)}
                        error={tagNameError != null}
                        helperText={tagNameError ?? (nameCheckState.checking ? 'Checking availability...' : 'Letters, numbers, and underscores only.')}
                        InputProps={{
                            endAdornment: nameCheckState.checking ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={18} />
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                </CardContent>
            </Card>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="h5">Requirements</Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={addRequirement}
                    disabled={!canAddRequirement}
                >
                    Add requirement
                </Button>
            </Stack>

            {requirements.map((requirement, index) => {
                const typeInfo = getTypeInfo(requirement.type);
                if (typeInfo == null) return null;

                const operators = getOperatorsForType(options, typeInfo.dataType);
                const selectedPredefinedOptions = getSelectedPredefinedOptions(typeInfo, requirement);
                const typeOptions = getTypeInfos();

                return (
                    <Card key={requirement.id}>
                        <CardContent>
                            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2} mb={2}>
                                <Typography variant="h6">Requirement {index + 1}</Typography>
                                <IconButton
                                    color="error"
                                    onClick={() => removeRequirement(requirement.id)}
                                    disabled={requirements.length === 1}
                                >
                                    <DeleteOutline />
                                </IconButton>
                            </Stack>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={5}>
                                    <FormControl fullWidth>
                                        <InputLabel>Requirement type</InputLabel>
                                        <Select
                                            label="Requirement type"
                                            value={requirement.type}
                                            onChange={(event) => onRequirementTypeChanged(requirement.id, event.target.value)}
                                        >
                                            {typeOptions.map(option => (
                                                <MenuItem key={option.key} value={option.key}>
                                                    {humanizeEnum(option.key)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Operator</InputLabel>
                                        <Select
                                            label="Operator"
                                            value={requirement.operator}
                                            onChange={(event) => onRequirementOperatorChanged(requirement.id, event.target.value)}
                                        >
                                            {operators.map(operator => (
                                                <MenuItem key={operator} value={operator}>
                                                    {getOperatorLabel(operator)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 0, md: 1.5 } }}>
                                        Type: {humanizeEnum(typeInfo.dataType)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid item xs={12}>
                                    {typeInfo.predefinedValues.length > 0 ? (
                                        isMultiValueOperator(requirement.operator) ? (
                                            <Autocomplete
                                                multiple
                                                options={typeInfo.predefinedValues}
                                                value={selectedPredefinedOptions}
                                                isOptionEqualToValue={(option, value) => getPredefinedOptionKey(option) === getPredefinedOptionKey(value)}
                                                getOptionLabel={(option) => option.displayName}
                                                onChange={(_, nextValue) => updateRequirement(requirement.id, currentRequirement => ({
                                                    ...currentRequirement,
                                                    selectedPredefinedKeys: nextValue.map(getPredefinedOptionKey),
                                                }))}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Values"
                                                        helperText={requirement.operator === 'BETWEEN'
                                                            ? 'Select exactly two predefined values (min, max).'
                                                            : requirement.operator === 'NIN'
                                                                ? 'Select one or more values to exclude (e.g. countries not in this list).'
                                                                : 'Select one or more (e.g. multiple countries).'}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <Autocomplete
                                                options={typeInfo.predefinedValues}
                                                value={selectedPredefinedOptions[0] ?? null}
                                                isOptionEqualToValue={(option, value) => getPredefinedOptionKey(option) === getPredefinedOptionKey(value)}
                                                getOptionLabel={(option) => option.displayName}
                                                onChange={(_, nextValue) => updateRequirement(requirement.id, currentRequirement => ({
                                                    ...currentRequirement,
                                                    selectedPredefinedKeys: nextValue == null ? [] : [getPredefinedOptionKey(nextValue)],
                                                }))}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Value"
                                                        helperText={typeInfo.dataType === 'string'
                                                            ? 'For multiple values, switch operator to In list or Not in list.'
                                                            : 'Select a predefined value.'}
                                                    />
                                                )}
                                            />
                                        )
                                    ) : typeInfo.dataType === 'duration' ? (
                                        isMultiValueOperator(requirement.operator) ? (
                                            <Stack gap={2}>
                                                {requirement.durationValues.map((durationValue, durationIndex) => (
                                                    <Stack key={`${requirement.id}-${durationIndex}`} direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                                        <Box flex={1}>
                                                            {renderDurationInputs(durationValue, (field, nextValue) => updateRequirement(requirement.id, currentRequirement => ({
                                                                ...currentRequirement,
                                                                durationValues: currentRequirement.durationValues.map((existingValue, existingIndex) =>
                                                                    existingIndex === durationIndex ? { ...existingValue, [field]: nextValue } : existingValue
                                                                ),
                                                            })))}
                                                        </Box>
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => updateRequirement(requirement.id, currentRequirement => ({
                                                                ...currentRequirement,
                                                                durationValues: currentRequirement.durationValues.filter((_, existingIndex) => existingIndex !== durationIndex),
                                                            }))}
                                                            disabled={requirement.durationValues.length === 1}
                                                        >
                                                            <DeleteOutline />
                                                        </IconButton>
                                                    </Stack>
                                                ))}
                                                <Box>
                                                    <Button
                                                        variant="text"
                                                        startIcon={<AddCircleOutline />}
                                                        disabled={requirement.operator === 'BETWEEN' && requirement.durationValues.length >= 2}
                                                        onClick={() => updateRequirement(requirement.id, currentRequirement => ({
                                                            ...currentRequirement,
                                                            durationValues: [...currentRequirement.durationValues, createEmptyDuration()],
                                                        }))}
                                                    >
                                                        Add duration value
                                                    </Button>
                                                </Box>
                                            </Stack>
                                        ) : (
                                            <>
                                                {renderDurationInputs(requirement.durationValue, (field, nextValue) => updateRequirement(requirement.id, currentRequirement => ({
                                                    ...currentRequirement,
                                                    durationValue: { ...currentRequirement.durationValue, [field]: nextValue },
                                                })))}
                                                <FormHelperText>
                                                    Use days, hours, and minutes. The dashboard converts this automatically to the API duration format.
                                                </FormHelperText>
                                            </>
                                        )
                                    ) : (
                                        <TextField
                                            label={isMultiValueOperator(requirement.operator) ? 'Values' : 'Value'}
                                            fullWidth
                                            type={typeInfo.dataType === 'numeric' ? 'number' : 'text'}
                                            value={isMultiValueOperator(requirement.operator) ? requirement.textValues : requirement.textValue}
                                            onChange={(event) => updateRequirement(requirement.id, currentRequirement => ({
                                                ...currentRequirement,
                                                [isMultiValueOperator(requirement.operator) ? 'textValues' : 'textValue']: event.target.value,
                                            }))}
                                            helperText={isMultiValueOperator(requirement.operator)
                                                ? (requirement.operator === 'BETWEEN'
                                                    ? 'Enter exactly two values separated by comma: min,max.'
                                                    : 'Separate multiple values with commas.')
                                                : 'Provide a single value.'}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}
